import { RouteErrorBoundary } from '@shared/ui';
import { NotificationProvider } from '@/context/notificationContext';
import { IndependenceConfirmationProvider } from '@/context/independenceContext';
import { PageTitleManager } from '@features/page-title';
import Loading from '@/components/ui-components/Loading';
import { Suspense } from 'react';
import UserWrapper from '@components/UserWrapper';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import { lazyWithRetry } from './lazyWithRetry';
import { privatePaths } from './Navigation';
import PrivateWrapper from './PrivateWrapper';

/**
 * Lazy-loaded route components. The `webpackChunkName` names are shared with `./Navigation`, which
 * points at several of these same modules — matching names put them in ONE chunk, a mismatch emits
 * the module twice. Change a name here and there together.
 */
const AdminPlatform = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@/Routers/AdminPlatform'),
);
const ForgotPassword = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '@pages/auth/ForgotPassword'),
);
const DashboardLayout = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../components/DashboardLayout'),
);
const AuthConsume = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/AuthConsume'),
);
const Login = lazyWithRetry(() => import(/* webpackChunkName: "auth" */ '../pages/auth/Login'));
const LoginEmailSend = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginEmailSend'),
);
const LoginPassword = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginPassword'),
);
const LoginPhone = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginPhone'),
);
const LoginPhoneSms = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginPhoneSms'),
);
const RouterWrapper = lazyWithRetry(
  () => import(/* webpackChunkName: "landing" */ './RouterWrapper'),
);
const PublicDocumentAccess = lazyWithRetry(
  () => import(/* webpackChunkName: "public-document" */ '../pages/public/PublicDocumentAccess'),
);
const PublicDocumentView = lazyWithRetry(
  () => import(/* webpackChunkName: "public-document" */ '../pages/public/PublicDocumentView'),
);

/**
 * THE AUDIT DASHBOARD IS ONE ROUTE (#540). Each section had a `<Route>` of its own here, each with
 * its own inline component rendering its own `DashboardLayout`, so a click in the sidebar unmounted
 * the aside and the header and mounted new ones. A section's first (lazy) render then suspended up
 * to the boundary above, which swapped the whole page for a spinner — every section "blinked" on
 * its first visit. One route over every dashboard path keeps one layout mounted across sections;
 * the layout's own boundary catches a section while it loads.
 */
export const DASHBOARD_PATHS = [
  '/dashboard/:id/overview',
  '/dashboard/:id/requests',
  '/dashboard/:id/requests/:requestId',
  '/dashboard/:id/status',
  '/dashboard/:id/documents',
  '/dashboard/:id/data-analysis',
  '/dashboard/:id/documents/:documentId',
  '/dashboard/:id/meetings',
  '/dashboard/:id/meetings/:meetingId',
  '/dashboard/:id/settings',
  '/dashboard/:id/team-role/edit/:idRole',
  '/dashboard/:id/role/add',
];

const MainRouter = () => (
  <Router>
    <Suspense fallback={<Loading position='absoluteCenter' />}>
      <Switch>
        {/* Public Routes */}
        <Route exact path='/' component={RouterWrapper} />
        <Route exact path='/revision' component={RouterWrapper} />
        <Route exact path='/team' component={RouterWrapper} />
        <Route exact path='/imprint' component={RouterWrapper} />
        <Route exact path='/data_protection' component={RouterWrapper} />
        <Route exact path='/login' component={Login} />
        <Route exact path='/login/phone' component={LoginPhone} />
        <Route exact path='/login/phone/sms' component={LoginPhoneSms} />
        <Route exact path='/login/new-password' component={LoginPassword} />
        <Route exact path='/login/email' component={ForgotPassword} />
        <Route exact path='/login/email/send' component={LoginEmailSend} />
        <Route exact path='/thank-you' component={RouterWrapper} />
        <Route exact path='/kontakt/:slug?' component={RouterWrapper} />
        <Route exact path='/partner' component={RouterWrapper} />
        <Route exact path='/faq' component={RouterWrapper} />
        <Route exact path='/consume' component={AuthConsume} />

        <Route exact path='/public/document/:id/:code/access' component={PublicDocumentAccess} />
        <Route exact path='/public/document/:id/:code/view' component={PublicDocumentView} />

        {/* Private Routes */}
        <PrivateWrapper exact path={privatePaths}>
          <NotificationProvider>
            <IndependenceConfirmationProvider>
              <PageTitleManager />
              <RouteErrorBoundary>
                <Suspense fallback={<Loading position='absoluteCenter' />}>
                  <Switch>
                    {/* Routes that require user data */}
                    <Route
                      exact
                      path='/audits'
                      component={(props) => (
                        <UserWrapper>
                          <AdminPlatform {...props} />
                        </UserWrapper>
                      )}
                    />
                    <Route
                      exact
                      path='/company/settings'
                      component={(props) => (
                        <UserWrapper>
                          <AdminPlatform {...props} />
                        </UserWrapper>
                      )}
                    />
                    <Route
                      exact
                      path='/company/:id/:path'
                      component={(props) => (
                        <UserWrapper>
                          <AdminPlatform {...props} />
                        </UserWrapper>
                      )}
                    />
                    <Route
                      exact
                      path='/notifications'
                      component={(props) => (
                        <UserWrapper>
                          <AdminPlatform {...props} />
                        </UserWrapper>
                      )}
                    />
                    <Route
                      exact
                      path='/account/settings'
                      component={(props) => (
                        <UserWrapper>
                          <AdminPlatform {...props} />
                        </UserWrapper>
                      )}
                    />
                    <Route
                      exact
                      path='/admin/:path'
                      component={(props) => (
                        <UserWrapper>
                          <AdminPlatform {...props} />
                        </UserWrapper>
                      )}
                    />
                    <Route
                      exact
                      path='/admin/companies/:type/:id/:path'
                      component={(props) => (
                        <UserWrapper>
                          <AdminPlatform {...props} />
                        </UserWrapper>
                      )}
                    />
                    {/* One route for the whole audit dashboard (#540) — see DASHBOARD_PATHS. */}
                    <Route
                      exact
                      path={DASHBOARD_PATHS}
                      render={(props) => (
                        <UserWrapper>
                          <DashboardLayout {...props} />
                        </UserWrapper>
                      )}
                    />
                  </Switch>
                </Suspense>
              </RouteErrorBoundary>
            </IndependenceConfirmationProvider>
          </NotificationProvider>
        </PrivateWrapper>
      </Switch>
    </Suspense>
  </Router>
);

export default MainRouter;
